import React from "react";
import classNames from "classnames";
import { allocation } from "@planzone/dto";
import { ResourceAvatar } from "@common/resourceAvatar";
import { ActivityWatcher } from "@common/activityWatcher";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import {
  useAllocateResources,
  useRemoveAllocation,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { AddAllocationIcon } from "@common/assets/addAllocationIcon";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ScreenWatcher } from "@common/screenWatcher";
import { Props } from "./types";
import styles from "./styles.scss";

function AllocationsList({ allocations, project, task }: Props) {
  const { isRequestPending, allocateResources } = useAllocateResources(task);
  const { removeAllocation } = useRemoveAllocation({
    ...allocation,
    uuid: "",
  });

  return (
    <Safeguard useCases={[UseCases.allocateResources]} context={{ task }}>
      {({ canAllocateResources }) => (
        <div className={styles.allocationsList}>
          <VisibilitySwitch
            renderChildren={(isVisible, show, hide) => (
              <React.Fragment>
                <ul
                  className={styles.list}
                  onClick={(e) => {
                    if (canAllocateResources) {
                      e.stopPropagation();
                      (isVisible ? hide : show)();
                    }
                  }}
                >
                  <ActivityWatcher
                    blur={hide}
                    focus={show}
                    className={styles.main}
                  >
                    <React.Fragment>
                      {allocations.slice(0, 2).map(({ resource }) => (
                        <li key={resource.uuid} className={styles.allocation}>
                          <ResourceAvatar resource={resource} />
                        </li>
                      ))}
                      {allocations.slice(2).length > 0 && (
                        <li
                          className={classNames(
                            styles.allocation,
                            styles.summary,
                          )}
                        >
                          <p>+{allocations.slice(2).length}</p>
                        </li>
                      )}
                      {canAllocateResources && allocations.length === 0 && (
                        <li className={styles.allocation}>
                          <div className={styles.menu}>
                            <div>
                              <AddAllocationIcon
                                className={styles.addAllocationIcon}
                              />
                            </div>
                          </div>
                        </li>
                      )}
                    </React.Fragment>
                  </ActivityWatcher>
                </ul>
                {isVisible && (
                  <LayoutWatcher
                    renderChildren={({ top }) => (
                      <ScreenWatcher
                        renderChildren={(screen) => (
                          <div
                            className={classNames(styles.resourcesList, {
                              [styles.up]: 310 + top >= screen.height,
                            })}
                          >
                            <div
                              className={styles.overlay}
                              onClick={(e) => {
                                e.stopPropagation();
                                hide();
                              }}
                            />
                            <div
                              className={styles.quickAllocationMenu}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <ResourceAllocationMenu
                                allocatedResources={allocations.map(
                                  (allocation) => allocation.resource,
                                )}
                                select={(resources) => {
                                  !isRequestPending &&
                                    allocateResources({
                                      allocations: resources.map(
                                        (resource) => ({
                                          ...allocation,
                                          resource,
                                        }),
                                      ),
                                      task,
                                      project,
                                    });
                                }}
                                unSelect={(resources) =>
                                  resources.forEach(({ uuid }) => {
                                    const allocation = allocations.find(
                                      (allocation) =>
                                        allocation.resource.uuid === uuid,
                                    );

                                    allocation && removeAllocation(allocation);
                                  })
                                }
                                openedViews={["resources"]}
                              />
                            </div>
                          </div>
                        )}
                      />
                    )}
                  />
                )}
              </React.Fragment>
            )}
          />
        </div>
      )}
    </Safeguard>
  );
}

export { AllocationsList };
